// Copyright 2023, Common Good Learning Tools LLC
import { mapState, mapGetters } from 'vuex'

export default {
	data() { return {
	}},
	methods: {
		start_resource_import() {
			//////////////////////////////////
			// Prompt for import file

			this.$prompt({
				title: 'Import Resources',
				text: 'Choose a CSV- or TSV-formatted file specifying resources, metadata, and/or existing alignments (see link below for sample import file). When you click the “PROCESS FILE” button, the file will be evaluated, and you’ll be asked to confirm that you wish to import the resources found in the file.<div class="mt-3 text-center"><a href="https://docs.google.com/spreadsheets/d/12mBOMDIEM0HmrncVFJZudOsf3rHAEm_AufOR3REPHhQ" target="_blank"><b>Sample Import Files</b></a></div>',
				// text: 'Choose a CSV- or TSV-formatted file specifying the resources you wish to align. For each resource, you must specify at least a resource ID. You may also specify a title, description, extra text, URL, and education level. When you click the “PROCESS FILE” button, the file will be evaluated, and you’ll be asked to confirm that you wish to import the resources found in the file.',
				promptType: 'file',
				acceptText: 'Process File',
				dialogMaxWidth: 550,
			}).then(file => {
				if (empty(file) || empty(file.name)) return
				// we receive a file from dialog-promise-pwet here; create a FileReader
				let reader = new FileReader()

				//////////////////////////////////
				// Read file and parse resource rows

				reader.onload = e => {
					let text = e.target.result 

					this.process_input_file(text)
				}
				// trigger the FileReader to load the text of the file
				reader.readAsText(file)
			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		async process_input_file(file_text) {
			// DEBUG
			if (empty(file_text)) file_text = window.file_text
			window.file_text = file_text

			let associations_for = {}

			// parse file_text; if first line includes a tab, assume it's a TSV; otherwise assume CSV
			let table
			if (file_text.split('\n')[0].indexOf('\t') > -1) {
				table = TSV.parse(file_text)
			} else {
				table = CSV.parse(file_text)
			}
			// console.log(table)

			if (table.length == 1) {
				this.$alert('The uploaded file included only one line. A resource import must include at least one line of headers and at least one line specifying a resource.')
				return
			}

			/* "Schema" for imports:
				resource_id
				title
				description
				full_content
				keywords
				url
				educationLevel
				cfdocument_identifier
				cfitem_identifier OR item_code
				is_primary_aligned_competency
				primary_aligned_competency
			*/

			// parse headers in line 0
			let fields = []
			for (let original_val of table[0]) {
				let val = original_val.toLowerCase()
				let field
				if (val == 'id' || val == 'resource_id' || val == 'external_resource_id') field = 'external_resource_id'
				else if (val == 'cfdocument_identifier') field = 'cfdocument_identifier'
				else if (val == 'cfitem_identifier') field = 'cfitem_identifier'
				else if (val == 'is_primary_aligned_competency') field = 'is_primary_aligned_competency'
				else if (val == 'primary_aligned_competency') field = 'primary_aligned_competency'
				else if (val.indexOf('item_code') > -1) field = 'item_code'
				else if (val.indexOf('title') > -1) field = 'resource_title'
				else if (val.indexOf('description') > -1) field = 'description'
				else if (val.indexOf('full_content') > -1) field = 'full_content'
				else if (val.indexOf('text') > -1 || val.indexOf('keywords') > -1) field = 'text'
				else if (val.indexOf('url') > -1) field = 'url'
				else if (val.indexOf('level') > -1) field = 'educationLevel'
				else if (val.indexOf('alignments:') > -1) field = val		// 'alignments:' followed by a framework_id
				else if (val.indexOf('alignment') > -1) field = 'alignments'
				else if (val.indexOf('ext:') == 0) field = original_val		// custom extensions; use original_val to preserve case
				if (empty(field)) {
					this.$alert(sr('Unknown field specified in header row: “$1”', original_val))
					return
				}
				fields.push(field)
			}
			console.log(fields)

			// now parse resource rows: for each non-header row...
			let resources = []
			for (let i = 1; i < table.length; ++i) {
				if (empty(table[i].join(''))) {
					continue
				}
				let r = {}
				if (!empty(this.resource_set_id)) {
					r.resource_set_id = this.resource_set_id
				}
				// for each column in the row...
				for (let j = 0; j < table[i].length; ++j) {
					// if value isn't empty, set that column's field to the value
					if (!empty(table[i][j])) {
						r[fields[j]] = table[i][j]
					}
				}

				// at the least, an ID must be specified
				if (empty(r.external_resource_id)) {
					console.log(r)
					this.$alert(`The resource in row ${i} does not have an ID specified. A resource ID must be specified for each resource.`)
					return
				}

				// educationLevel must be an array; allow for comma-separated values
				if (r.educationLevel) {
					r.educationLevel = r.educationLevel.split(/\s*,\s*/)

					// if a single value, see if it's a range, and split if so
					if (r.educationLevel.length == 1) {
						// note that grade_level_display_to_educationLevel should take care of dealing with, e.g. 'K' instead of 'KG' and '9' instead of '09'
						let arr = U.grade_level_display_to_educationLevel(r.educationLevel[0])
						if (arr.length > 0) r.educationLevel = arr
					} else {
						// code according to CASE convention if possible
						for (let i = 0; i < r.educationLevel.length; ++i) {
							let ei = U.educationLevel_index(r.educationLevel[i])
							if (!empty(ei)) r.educationLevel[i] = vapp.$store.state.grades[ei].value
						}
					}
				}

				// check format for/parse `alignments` column if we have it; format for this should be:
				// framework_identifier_1:item_identifier_1, framework_identifier_1:item_identifier_2, framework_identifier_2:item_identifier_3
				if (r.alignments) {
					let aarr = r.alignments.split(/\s*,\s*/)
					r.alignments = []
					for (let j = 0; j < aarr.length; ++j) {
						let arr = aarr[j].split(/\s*:\s*/)
						if (arr.length != 2) {
							this.$alert(`Improperly formatted alignments for resource ID ${r.external_resource_id}: ${aarr[j]}`)
							return
						}
						let framework_identifier = arr[0]
						let item_identifier = arr[1]

						// don't push duplicate alignments
						if (r.alignments.find(x=>x.fi == framework_identifier && x.ii == item_identifier)) {
							console.warn('skipping duplicate alignment (1)', item_identifier)
							continue
						}

						// TODO: if item_identifier isn't a guid, assume it's a humanCodingScheme, and try to find it in the given framework
						r.alignments.push({fi: framework_identifier, ii: item_identifier})
					}
				}

				// check for/parse cfdocument_identifier/cfitem_identifier columns for importing alignments
				if (r.cfdocument_identifier && (r.cfitem_identifier || r.item_code)) {
					if (empty(r.alignments)) r.alignments = []
					
					let framework_identifier = r.cfdocument_identifier
					let item_identifier = r.cfitem_identifier

					// if we receive an item_code, try to find it in the given framework
					if (empty(item_identifier) && !empty(r.item_code)) {
						let fr = await U.load_framework_and_cfo(framework_identifier)
						if (empty(fr?.json.CFItems)) {
							console.warn(`couldn’t find framework for ${framework_identifier} (${r.item_code})`)
							// in this case, for now at least, save the code as the item identifier ??
							item_identifier = r.item_code
						} else {
							let cfitem = fr.json.CFItems.find(x=>x.humanCodingScheme == r.item_code)
							if (!empty(cfitem)) {
								// found it!
								item_identifier = r.item_code
							} else {
								console.warn(`found framework (${framework_identifier}) but couldn’t find item for code (${r.item_code})`)
								// in this case, for now at least, save the code as the item identifier ??
								item_identifier = r.item_code
							}
						}
					}

					// if this row is designated as is_primary_aligned_competency 1 (or 'Y', or any non-empty value except 0),
					if (!empty(r.is_primary_aligned_competency) && r.is_primary_aligned_competency != 0) {
						// if we find the item
						let fr = await U.load_framework_and_cfo(framework_identifier)
						if (empty(fr?.json.CFItems)) {
							console.warn(`couldn’t find alignedCompetency framework for ${framework_identifier}`)
						} else if (empty(fr.cfo.cfitems[item_identifier])) {
							console.warn(`couldn’t find alignedCompetency item for ${item_identifier}`)
						} else {
							// store the aligned fullStatement as the primary_aligned_competency
							r.primary_aligned_competency = $.trim(`${fr.cfo.cfitems[item_identifier].humanCodingScheme??''} ${fr.cfo.cfitems[item_identifier].fullStatement}`)
						}
					}
					// NOTE: you can also specify the primary_aligned_competency text directly in a column

					// don't push duplicate alignments
					if (r.alignments.find(x=>x.fi == framework_identifier && x.ii == item_identifier)) {
						console.warn('skipping duplicate alignment (2)', item_identifier)
						continue
					}

					r.alignments.push({fi: framework_identifier, ii: item_identifier})
					delete r.cfdocument_identifier
					delete r.cfitem_identifier
					delete r.item_code
				}

				// if we got `alignments:xxx` columns, format those
				// TODO 5/4/2025: not sure if we want to support this format long-term; not maintaining it for now...
				for (let f of fields) {
					if (f.indexOf('alignments:') > -1) {
						if (!empty(r[f])) {
							let framework_identifiers = f.replace(/alignments:\s*/, '').split(/\s*,\s*/)
							for (let framework_identifier of framework_identifiers) {
								let framework_record = this.framework_records.find(x=>x.lsdoc_identifier == framework_identifier)

								if (empty(framework_record)) {
									this.$alert(`Your import file specifies alignments to a framework with GUID ${framework_identifier}, but this framework could not be identified.`)
									return
								}

								// if we don't already have this framework fully loaded, load it now
								if (!framework_record.framework_json_loaded && !framework_record.framework_json_loading) {
									await U.load_framework_and_cfo(framework_identifier)
								}

								if (empty(associations_for[framework_identifier])) associations_for[framework_identifier] = 0

								let aarr = r[f].split(/\s*,\s*/)
								if (empty(r.alignments)) r.alignments = []
								for (let item_identifier of aarr) {
									// if it's not a GUID, assume it's a HCS value, and try to look it up
									if (!U.is_uuid(item_identifier)) {
										let item = framework_record.json.CFItems.find(x=>x.humanCodingScheme == item_identifier)
										if (item) {
											item_identifier = item.identifier
										} else {
											console.warn(`no item found in ${framework_identifier} for hcs: ${item_identifier}`)
											continue
										}
									
									// else look up in cfitems hash
									} else if (empty(framework_record.cfo.cfitems[item_identifier])) {
										// console.warn(`no item found in ${framework_identifier} for GUID: ${item_identifier}`)
										continue
									}
									// console.log(`found associated item for ${framework_identifier} / ${item_identifier}`)

									// don't push duplicate alignments
									if (r.alignments.find(x=>x.fi == framework_identifier && x.ii == item_identifier)) {
										console.warn('skipping duplicate alignment (3)', item_identifier)
										continue
									}

									r.alignments.push({fi: framework_identifier, ii: item_identifier})
									++associations_for[framework_identifier]
								}
							}
						}
						delete r[f]
					}
				}

				// if we get a resource ID from earlier in this import file, add to it; this lets us use different lines for alignments
				let existing = resources.find(x=>x.external_resource_id == r.external_resource_id)

				// if this is a new resource (in this file)...
				if (!existing) {
					// and we're in v1/v2 resource alignment system
					if (this.resource_set) {
						// if we don't have a title, make one up, unless the resource already exists in the set??
						if (!r.resource_title) {
							if (!this.resources.find(x=>x.external_resource_id == r.external_resource_id)) {
								// OR skip it if it doesn't have a title...
								console.warn(`Skipping new resource with no title provided (${r.external_resource_id})`)
								continue
								r.resource_title = `resource ${r.external_resource_id}`
							}
						}
					}

					// then push to resources
					resources.push(r)
				
				// else it already exists in the resources array; update in place
				} else {
					// update existing with the fields from r, merging alignments if there
					for (let key in r) {
						if (key == 'alignments') {
							if (empty(existing.alignments)) existing.alignments = []
							for (let a of r.alignments) {
								// don't push duplicate alignments
								if (existing.alignments.find(x=>x.fi == a.fi && x.ii == a.ii)) {
									console.warn('skipping duplicate alignment (4)', r.resource_id, a.ii)
									continue
								}
								existing.alignments.push(a)
							}
						} else {
							existing[key] = r[key]
						}
					}
				}
			}
			// console.log('associations_for', associations_for)

			//////////////////////////////////
			// prepare a brief report for the resources that would be uploaded
			let new_resources = []
			let updated_resources = []
			let fr

			// "OG" resource set: send resources to the server to check if any have been already imported
			if (this.resource_set && !this.is_case_resource_set) {
				let payload = {
					service_url: 'save_resources',
					// we stringify the resources in case it's a lot of data
					resources: JSON.stringify(resources),
					user_id: this.user_info.user_id,
					framework_identifier: this.framework_identifier,
					check_only: 'yes',
					return_resources: 'no',
					return_resource_alignments: 'no',
					return_raw_alignments: 'no',
				}
				U.loading_start()
				let result = await this.$store.dispatch('service', payload)
				U.loading_stop()

				for (let external_resource_id in result.resource_status) {
					if (result.resource_status[external_resource_id] == 'new') {
						new_resources.push(external_resource_id)
					} else {
						updated_resources.push(external_resource_id)
					}
				}

			// CASE-coded resource set: check framework to check if any resources have been already imported
			} else {
				// set framework_record; this is different for v2 vs scram
				if (this.resource_set) fr = this.resource_set.framework_record
				else fr = this.framework_record

				for (let r of resources) {
					let existing = fr.json.CFItems.find(cfi => U.is_resource_item(cfi) && Resource.resource_matches_case_item(r, cfi))
					if (existing) updated_resources.push(r.external_resource_id)
					else new_resources.push(r.external_resource_id)
				}

				// if the number of items in the current famework would exceed X, give a special warning
				let updated_n_items = fr.json.CFItems.length + new_resources.length
				if (updated_n_items > this.recommended_max_cfitems) {
					let title = 'Warning'
					let text = `This import will add ${new_resources.length} resources to this framework, which will make the total count of resources in the framework <b>${updated_n_items}</b>.`
						+ `<div class="mt-3">Frameworks with more than ${this.recommended_max_cfitems} items become difficult to manage, so we recommend that you create a new framework at this point rather than import these items to this framework.</div>`
						+ `<div class="mt-3">Click “PROCEED WITH IMPORT” to ignore this warning and proceed.</div>`
					let confirmed = await U.confirm_promise({
						title,
						text,
						acceptText: 'PROCEED WITH IMPORT',
						dialogMaxWidth: 600,
					})

					// if user clicks cancel, bail out
					if (!confirmed) return

					// user clicked OK, so continue processing
				}
			}

			let html = ''
			if (new_resources.length > 0) {
				html += `<div class="mb-3">${new_resources.length} ${U.ps('resource', new_resources.length)} will be <b>created</b></div>`
			}
			if (updated_resources.length > 0) {
				html += `<div class="mb-3">${updated_resources.length} ${U.ps('resource', updated_resources.length)} will be <b>updated</b></div>`
			}
			
			if (empty(html)) {
				this.$alert(`Something is malformed in the file you attempted to import from: no new resources or resource updates could be parsed.`)
				return
			}

			html += 'Would you like to proceed?'

			try {
				await this.$confirm({
					title: 'Import Resources',
					text: html,
					acceptText: 'Import Resources',
					dialogMaxWidth: 600
				})
			} catch(e) {
				console.warn('canceled')
				return
			}

			//////////////////////////////////
			// "OG" resource set: send resources back to the server to be imported
			if (this.resource_set && !this.is_case_resource_set) {
				U.loading_start()
				let payload = {
					service_url: 'save_resources',
					// we stringify the resources in case it's a lot of data
					resources: JSON.stringify(resources),
					user_id: this.user_info.user_id,
					framework_identifier: this.framework_identifier,
					check_only: 'no',
					return_resources: 'yes',
					return_resource_alignments: 'yes',
					return_raw_alignments: 'no',
				}
				this.$store.dispatch('service', payload).then((result)=>{
					U.loading_stop()

					//////////////////////////////////
					// add/replace resources to the resource_set
					for (let resource of result.resources) {
						resource = new Resource(resource)
						let i = this.resources.findIndex(x=>x.resource_id == resource.resource_id)
						if (i == -1) this.$store.commit('set', [this.resources, 'PUSH', resource])
						else this.$store.commit('set', [this.resources, 'SPLICE', i, resource])
					}

				}).catch((result)=>{
					U.loading_stop()
					this.$alert('<b class="red--text">An error occurred:</b> ' + result.error)
				}).finally(()=>{})

			// CASE-coded resource set: import to framework
			} else {
				let data = {
					update_item_types: true,
					lsdoc_identifier: fr.lsdoc_identifier,
					CFItems: [],
					CFAssociations: [],
					check_out_and_in: 'yes',		// require check_out_and_in; this saves an archive and prevents this from being done if someone else is editing the framework
					show_spinner: true,
				}

				// if we're importing new items, establish a top-level "folder" for the imported items
				let new_item_sequence = 1
				let folder_cfitem
				if (new_resources.length > 0) {
					folder_cfitem = new CFItem({
						fullStatement: `Imported resources: ${date.format(new Date(), 'MMM D, YYYY h:mm A')}`
					})
					folder_cfitem.complete_data(fr.json.CFDocument)
					data.CFItems.push(folder_cfitem.to_json())

					let folder_sequence
					if (fr.cfo.cftree.children.length == 0) folder_sequence = 1
					else {
						folder_sequence = fr.cfo.cftree.children[fr.cfo.cftree.children.length-1].sequence
						if (empty(folder_sequence)) folder_sequence = 1
						else folder_sequence = folder_sequence + 1
					}
					let folder_association = U.create_association(fr.json.CFDocument, folder_cfitem, folder_sequence, fr.json.CFDocument)
					data.CFAssociations.push(folder_association.to_json_for_update())
				}

				// now go through each to-be-imported resource
				for (let resource of resources) {
					// convert to Resource class, so we can access the class functions (but we need to preserve the original resource object too; see below)
					let rc = new Resource(resource)
					rc.framework_record = fr
					let cfitem

					// if the item already exists in the framework, we want to update the existing cfitem with the incoming data
					if (updated_resources.includes(resource.external_resource_id)) {
						cfitem = fr.json.CFItems.find(cfi => U.is_resource_item(cfi) && Resource.resource_matches_case_item(resource, cfi))
						cfitem = new CFItem(cfitem)
						rc.update_cfitem(cfitem)

						// prepare for sending to the server
						cfitem = cfitem.to_json_for_update()

					// else it's a new item
					} else {
						// start with the cfitem data for resource, then complete it with the item type, identifier, and uri
						cfitem = new CFItem(rc.cfitem_for_save())
						cfitem.CFItemType = 'Learning Resource'
						cfitem.complete_data(fr.json.CFDocument)

						// prepare for sending to the server
						cfitem = cfitem.to_json()

						// add an association to tie the item to the newly-created folder
						let child_association = U.create_association(folder_cfitem, cfitem, new_item_sequence, fr.json.CFDocument)
						data.CFAssociations.push(child_association.to_json())

						++new_item_sequence
					}

					// add the cfitem data to data
					data.CFItems.push(cfitem)

					// now if we received alignments, add them (note we use resource here, not rc, because framework and item identifiers are coded as fi and ii)
					if (resource.alignments) for (let alignment of resource.alignments) {
						// TODO: support crosswalk frameworks; for now we're saving in the resource framework

						// if the alignment already exists, skip it
						if (updated_resources.includes(resource.external_resource_id)) {
							let assocs = fr.cfo.associations_hash[cfitem.identifier]
							if (assocs && assocs.find(x=>x.associationType == 'ext:isAlignedTo' && x.destinationNodeURI.identifier == alignment.ii)) {
								console.warn('skipping already-imported alignment')
								continue
							}
						}
						// console.warn('adding alignment')
						
						let assoc = {
							// left node is the resource
							originNodeURI: {
								title: `${cfitem.fullStatement} (:${fr.lsdoc_identifier}:)`,
								identifier: cfitem.identifier,
								uri: U.generate_child_uri(fr.json.CFDocument, cfitem.identifier, 'CFItems'),
							},

							// right_node_uri is the aligned cfitem; construct this below

							associationType: 'ext:isAlignedTo',
						}

						// try to get the framework record for alignment.fi
						let aligned_to_cfitem
						let aligned_framework_record = this.framework_records.find(x=>x.lsdoc_identifier == alignment.fi)
						if (aligned_framework_record) {
							// if we got it, load the framework if necessary
							try {
								await U.load_framework_and_cfo(aligned_framework_record.lsdoc_identifier)

								// and try to find the aligned-to cfitem
								aligned_to_cfitem = aligned_framework_record.cfo.cfitems[alignment.ii]

							} catch(e) {
								console.warn(`couldn’t load framework ${alignment.fi}`)
							}
						}

						// now construct the destinationNodeURI, from the aligned_to_cfitem if we have it
						if (aligned_to_cfitem) {
							assoc.destinationNodeURI = {
								title: U.generate_cfassociation_node_uri_title(aligned_to_cfitem) + ` (:${alignment.fi}:)`,
								identifier: aligned_to_cfitem.identifier,
								uri: aligned_to_cfitem.uri,
							}

						} else {
							// we don't know the aligned standard's info, so make a guess...
							assoc.destinationNodeURI = {
								title: `${alignment.ii} (:${alignment.fi}:)`,
								// the identifier will be correct, and that's the most important thing
								identifier: alignment.ii,
								uri: `${this.site_config.case_registry_name}/ims/case/v1p0/CFItems/${alignment.ii}`,
							}
						}

						// fill in other parts of the CFAssociation object
						assoc = new CFAssociation(assoc)
						assoc.complete_data(fr.json.CFDocument)	// this will add '*NOW*' as the lastChangeDateTime
						assoc = assoc.to_json()

						data.CFAssociations.push(assoc)
					}
				}

				/////////////////
				// send the data to the server!
				// console.log('data to send: ', data)
				// for (let cfi of data.CFItems) console.log(cfi.extensions)
				// return

				this.$store.dispatch('save_framework_data', data).then(()=>{
					this.$alert({
						text: 'Import successful. Please reload to view imported or updated resources.',
						acceptText: 'Reload',
					}).then(x=>{
						vapp.reload_window()
					})

					/*
					for (let assoc of data.CFAssociations) {
						if (assoc.associationType != 'isChildOf') continue	// we'll process alignment assocs below

						assoc.lastChangeDateTime = this.$store.state.framework_lastChangeDateTime

						// add to or update in the CFAssociations
						let index = fr.json.CFAssociations.findIndex(x=>x.identifier == assoc.identifier)
						if (index == -1) {
							this.$store.commit('set', [fr.json.CFAssociations, 'PUSH', assoc])
						} else {
							this.$store.commit('set', [fr.json.CFAssociations, 'SPLICE', index, assoc])
						}
					}

					// now for each added or updated cfitem...
					let folder_node
					for (let cfi of data.CFItems) {
						// create or update the CFItem(s) in the framework json
						let index = fr.json.CFItems.findIndex(x=>x.identifier == cfi.identifier)

						cfi.lastChangeDateTime = this.$store.state.framework_lastChangeDateTime

						// get a new clean json object
						let clean_json = new CFItem(cfi).to_json()

						if (index == -1) {
							this.$store.commit('set', [fr.json.CFItems, 'PUSH', object_copy(clean_json)])

							// add to cfo; if this is the inserted folder (which will always be the first item), add to the document
							if (!(U.is_resource_item(clean_json))) {
								folder_node = U.add_child_to_cfo(object_copy(clean_json), fr.cfo.cftree, this.$store, fr.cfo)
							} else {
								// else add to the folder_node
								U.add_child_to_cfo(object_copy(clean_json), folder_node, this.$store, fr.cfo)
							}

						} else {
							this.$store.commit('set', [fr.json.CFItems, 'SPLICE', index, object_copy(clean_json)])

							// transfer updates to the cfo cfitem; there are relatively few properties we deal with here, since most stuff is in extensions
							let cfo_cfitem = fr.cfo.cfitems[cfi.identifier]
							this.$store.commit('set', [cfo_cfitem, 'fullStatement', cfi.fullStatement])
							this.$store.commit('set', [cfo_cfitem, 'lastChangeDateTime', cfi.lastChangeDateTime])
							this.$store.commit('set', [cfo_cfitem, 'educationLevel', object_copy(cfi.educationLevel)])
							this.$store.commit('set', [cfo_cfitem, 'extensions', object_copy(cfi.extensions)])
						}

						// if this is a resource (as opposed to the folder containing the new resources)...
						if (U.is_resource_item(clean_json)) {
							// create a new resource object (if it's an existing resource, we'll just replace it)
							let rc = new Resource(object_copy(clean_json), fr)

							// add any imported alignments (existing resources might already have had some alignments; those will get recreated in the previous line)
							for (let assoc of data.CFAssociations) {
								if (assoc.associationType != 'ext:isAlignedTo') continue
								if (assoc.originNodeURI.identifier != clean_json.identifier) continue

								// add the assoc to json and the associations_hash
								assoc.lastChangeDateTime = this.$store.state.framework_lastChangeDateTime
								this.$store.commit('set', [fr.json.CFAssociations, 'PUSH', assoc])
								U.update_associations_hash(fr.cfo, assoc)

								// add alignment to the resource
								rc.alignments.push(new Resource_Alignment(assoc))
							}

							// now push or replace the resource in resource_set, if we're in the v1/v2 alignment system
							if (this.resource_set) {
								let index = this.resource_set.resources.findIndex(x=>x.resource_id == rc.resource_id)
								if (index == -1) this.$store.commit('set', [this.resource_set.resources, 'PUSH', rc])
								else this.$store.commit('set', [this.resource_set.resources, 'SPLICE', index, rc])
							}
						}
					}
					*/
				})
			}
		},

		start_resource_export() {
			// for now, only export the alignments for the currently-selected framework
			let arr = []

			// header row
			arr.push(['resource_id', 'cfdocument_identifier', 'cfitem_identifier'])
			for (let i = 0; i < this.resources_for_alignment.length; ++i) {
				let r = this.resources_for_alignment[i]
				for (let alignment of r.alignments) {
					if (alignment.framework_identifier == this.tba_framework_identifier) {
						arr.push([r.external_resource_id, alignment.framework_identifier, alignment.item_identifier])
					}
				}
			}
			// console.log(CSV.stringify(arr))

			let fr = this.framework_records.find(x=>x.lsdoc_identifier == this.tba_framework_identifier)
			let framework_title = fr.json.CFDocument.title
			if (arr.length == 0) {
				this.$alert(`No alignments to export for framework “${framework_title}”.`)
				return
			}

			this.$confirm({
				title: 'Export Alignments',
				text: `This will export a CSV file with ${arr.length - 1} alignments to the currently-selected framework (“${framework_title}”).`
					+ `<div class="mt-2">The first line of the CSV file will be a header specifying the three columns in the file:<ol>`
					+ `<li><b>resource_id</b>: a resource id (in the format you used when you uploaded the resources of this set)</li>`
					+ `<li><b>cfdocument_identifier</b>: the CASE identifier (GUID) for the to-be-aligned framework’s document</li>`
					+ `<li><b>cfitem_identifier</b>: the CASE identifier (GUID) for the aligned standard in this framework</li>`
					+ `</ol></div>`
					+ `<div class="mt-2">Note that the same resource_id will have multiple rows if that resource is aligned to multiple standards.</div>`,
				acceptText: 'Download Export File',
				acceptIconAfter: 'fas fa-file-export',
				dialogMaxWidth: 800,
			}).then(y => {
				let date_string = date.format(new Date(), 'YYYY-MM-DD')
				let filename = `${this.resource_set.resource_set_title}--Alignments--${framework_title}--${date_string}.csv`
				// console.log(filename)
				U.download_file(CSV.stringify(arr), filename)
			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		async resource_synch_to_cureum() {
			// we must have a tba_framework_identifier and a base_framework_identifier to do this
			if (empty(this.tba_framework_identifier)) {
				this.$alert('To export to cureum, you must choose a framework to align TO.')
				return
			}
			if (empty(this.base_framework_identifier)) {
				this.$alert('To export to cureum, you must choose a “base framework”.')
				return
			}

			let tba_title = this.tba_framework_record.json.CFDocument.title
			let base_title = this.base_framework_record.json.CFDocument.title

			// ask user if they want to remove previous alignments
			let val = await U.confirm_promise({
				title: 'Sync to Cureum',
				
				text: ''
					// PW: I initially wrote this, but now I think it's not accurate (see logic below)
					// + `<p>This action will update alignments in ${this.site_config.linked_cureum_instance} for any resources that:</p>`
					// + '<ol>'
					// + `<li class="mt-0">Have an alignment specified to the current to-be-aligned framework (“${tba_title}”), or</li>`
					// + `<li class="mt-0">Are explicitly marked as having <b>no alignment</b> to the to-be-aligned framework</li>`
					// + '</ol>'

					+ '<p>Would you like to:</p>'
					+ '<ul>'
					+ `<li class="mt-3"><b>UPDATE ONLY</b> the alignments to “${tba_title}”, leaving previous alignments to “${base_title}” in place, or </li>`
					+ `<li class="mt-3"><b>REPLACE</b> previous alignments to “${base_title}” for updated resources the with the newly-specified alignments to “${tba_title}”, removing the previous alignments</li>`
					+ '</ul>'
					,
				extraBtnText: 'UPDATE ONLY…',
				extraBtnIcon: 'fas fa-plus',
				acceptText: 'REPLACE…',
				acceptIcon: 'fas fa-swap',
				dialogMaxWidth: 720,
			})
			if (!val) return

			let delete_existing_alignments 
			// extra button: UPDATE ONLY (add), so delete_existing_alignments is false
			if (val === 'extra') delete_existing_alignments = false
			// accept button: REPLACE, so delete_existing_alignments is true
			else delete_existing_alignments = true

			/* send to cureum service:
				for each asset:
					asset_type
					asset_id
					tba_framework_identifier
					cfitem_ids to add -- [framework_identifier, item_identifier]
					cfitem_ids to remove -- [framework_identifier, item_identifier]
				hash of added cfitem data

				NOTE: Cureum will remove any previously-existing alignments to the tba_framework_identifier, 
				then add the alignments specified in added_cfitems.
				So we don't need to pay attention here to hasNoMatch assocs -- the UI enforces that to mark a resource with a hasNoMatch assoc, 
				the resource must not have any isAlignedTo assocs (or, if it did originally have some isAlignedTo's, they must have all been deleted)
			*/
			let assets = []
			let cfitem_json = {}
			let n_added = 0, n_removed = 0, n_assets_unchanged = 0

			let tba_fr = await U.load_framework_and_cfo(this.tba_framework_identifier, {show_loader:true})
			let bf_fr = await U.load_framework_and_cfo(this.base_framework_identifier, {show_loader:true})

			for (let i = 0; i < this.resources_for_alignment.length; ++i) {
				let r = this.resources_for_alignment[i]
				
				// start object for asset
				let o = {
					tba_framework_identifier: this.tba_framework_identifier,
					added_cfitems: [],
					removed_cfitems: [],
					// note that we just need to send item_identifiers in these arrays; the cfitem_json will include the framework_identifier for added cfitems
				}

				// add asset_id and asset_type
				let external_resource_id = r.external_resource_id ?? ''
				if (external_resource_id.search(/lesson-(\d+)/) > -1) {
					o.asset_id = RegExp.$1
					o.asset_type = 'lesson'
				} else if (external_resource_id.search(/resource-(.+)/) > -1) {
					o.asset_id = RegExp.$1
					o.asset_type = 'resource'
				} else {
					// fallback
					o.asset_id = r.cfitem.identifier
					o.asset_type = 'resource'
				}

				let alignment_changed = false

				/* if:
					UPDATE ONLY:
						TBA framework:
							If one or more alignments are specified, add those alignments and delete any previously-existing alignments to TBA
							If NOMATCH, OR (no TBA alignments are specified), delete pre-existing alignments to TBA
								This will be accomplished by sending the resource through with no alignments specified to the TBA framework 
									(receive_alignments_from_satchel always starts by removing all existing alignments to TBA; 
									then since there are no alignments being added, the resource won't have any alignments anymore)
						Base framework:
							Don't touch the Base alignments -- don't send these in removed or added
							
					REPLACE:
						TBA framework:
							Same as update
						Base framework:
							Delete all alignments to Base -- send all in removed
				*/

				for (let alignment of r.alignments) {
					// to be safe: filter ext:hasNoMatch assocs -- but such assocs shouldn't actually be in the r.alignments array
					if (alignment.cfassociation.associationType == 'ext:hasNoMatch') {
						console.warn('skipping ext:hasNoMatch')
						continue
					}

					// add tba alignments that AREN'T nomatch
					if (alignment.framework_identifier == this.tba_framework_identifier) {
						o.added_cfitems.push(alignment.item_identifier)
						++n_added
						alignment_changed = true

						// add cfitem to hash if we didn't already do so
						if (empty(cfitem_json[alignment.item_identifier])) {
							let cfi = tba_fr.json.CFItems.find(x=>x.identifier == alignment.item_identifier)
							if (empty(cfi)) {
								this.$alert(`TBA item ${alignment.item_identifier} could not be found.`)
								return
							}
							cfi = structuredClone(cfi)
							// add framework_identifier to cfi
							cfi.framework_identifier = this.tba_framework_identifier

							// and add sequenceNumber and childOf (note that we're ignoring aliases here)
							cfi.childOf = tba_fr.cfo.cfitems[cfi.identifier]?.tree_nodes[0]?.parent_node.cfitem.identifier
							cfi.sequenceNumber = tba_fr.cfo.cfitems[cfi.identifier]?.tree_nodes[0]?.sequence

							// currently we always add primary alignments here, so don't add is_secondary
							cfitem_json[alignment.item_identifier] = cfi
						}

					// if delete_existing_alignments is true, remove base alignments
					} else if (delete_existing_alignments) {
						if (alignment.framework_identifier == this.base_framework_identifier) {
							o.removed_cfitems.push(alignment.item_identifier)
							++n_removed
							alignment_changed = true
						}
					}
				}

				if (alignment_changed) {
					assets.push(o)
					// TEMP: only send one for testing purposes
					// break
				} else {
					++n_assets_unchanged
				}
			}

			if (n_added == 0 && n_removed == 0) {
				this.$alert('You have not specified any alignments to add or remove.')
				return
			}

			console.log('assets', assets)
			console.log('cfitem_json', cfitem_json)

			let msg = `This action will:<ul class="mb-3">`
				+ `<li class="mt-3"><b>ADD ${n_added}</b> alignment(s) to “${tba_fr.json.CFDocument.title}”</li>`
			
			if (delete_existing_alignments) {
				msg += `<li class="mt-1">and <b>REMOVE ${n_removed}</b> alignment(s) from “${bf_fr.json.CFDocument.title}”</li>`
			}

			msg += `<li class="mt-3">for <b>${assets.length} asset(s)</b>.</li>`
				+ `<li class="mt-1"><b>${n_assets_unchanged} asset(s)</b> will be UNCHANGED.</li>`
				+ `</ul>Would you like to proceed?`
				
			val = await U.confirm_promise({
				title: 'Confirm Sync to Cureum',
				text: msg,
				acceptText: 'Proceed',
				acceptIconAfter: 'fas fa-circle-arrow-right',
				dialogMaxWidth: 600,
			})
			if (!val) return
			// user confirmed

			// send data to service
			let payload = {
				user_id: this.user_info.user_id,
				// stringify assets and cfitem_json, and encode_blocked_keywords for the latter
				assets: JSON.stringify(assets),
				cfitem_json: U.encode_blocked_keywords(JSON.stringify(cfitem_json)),
				encode_blocked_keywords: ['cfitem_json']
			}
			U.loading_start()
			let result = await U.ajax_promise('send_alignments_to_cureum', payload)
			U.loading_stop()
			if (result.status == 'ok') {
				this.$alert('Cureum synch successful!')
			} else {
				this.$alert('An error occurred. See console.')
				console.error(result)
			}
		},
	}
}